// App Connection grant management (Decision 051) — shared plumbing for the
// `grid callers` / `grid calls` groups and the `grid debug call` helper.
//
// Decision 051 model: a callee declares `callers:` in cloudgrid.yaml, and a
// per-callee NetworkPolicy enforces the allow-list in-cluster. The caller
// grant is the approval record behind that policy — no tokens anywhere. The
// CLI wraps the v2 caller-grant routes:
//
//   GET    /entities/:id/caller-grants             inbound  (who may call me)
//   POST   /entities/:id/caller-grants/:g/approve  callee admin approves
//   POST   /entities/:id/caller-grants/:g/revoke   callee admin revokes
//   GET    /entities/:id/calling-grants            outbound (what I call)
//   DELETE /entities/:id/calling-grants/:g         caller withdraws
//
// Address grammar is single-sourced from `packages/shared` —
// `parseCallerAddress` accepts a bare app slug, `<grid-slug>/<app-slug>`
// (same-grid only in this release), or an entity id.

import { parseCallerAddress } from '../../../../shared/src/caller-address.js';
import type { CallerGrant } from '../../../../shared/src/v1-types.js';
import { getActiveOrgSlug } from '../../active-org.js';
import { readLink } from '../../services/link.js';
import type { IV2Client } from '../../api/v2-client.js';
import { V2ApiError } from '../../api/v2-errors.js';
import { CloudGridError } from '../../utils.js';
import type { V2RuntimeDetailResponse } from '../status.js';

// ── Address parsing ───────────────────────────────────────────────────────

export interface CallTarget {
  /** Slug or entity id, as accepted by `/api/v2/runtimes/:idOrName`. */
  idOrName: string;
  raw: string;
}

/**
 * Parse a target address. Accepts the three grid-based forms — a bare app
 * slug, `<grid-slug>/<app-slug>`, or an entity id.
 *
 * The grid form is guarded against the ACTIVE grid: the runtimes lookup is
 * scoped to the active grid, so a mismatched grid slug would otherwise be
 * silently discarded and resolve the active grid's app of the same name.
 * The API rejects cross-grid pairs (CROSS_ORG_NOT_SUPPORTED); the CLI
 * matches it here. When no active grid is configured the guard is skipped —
 * the authenticated lookup fails first anyway, and the server still
 * enforces the boundary.
 */
export function parseCallTarget(addr: string): CallTarget {
  const parsed = parseCallerAddress(addr);
  if (!parsed) {
    throw new CloudGridError(
      `${JSON.stringify(addr)} is not a valid app address. Use the app slug, <grid-slug>/<app-slug>, or the entity id (e.g. billing-service).`,
    );
  }
  if (parsed.form === 'grid') {
    const activeGrid = getActiveOrgSlug();
    if (activeGrid && parsed.gridSlug !== activeGrid) {
      throw new CloudGridError(
        `Cross-grid calls aren't allowed yet. '${addr}' is in grid '${parsed.gridSlug}'; your active grid is '${activeGrid}'.`,
      );
    }
  }
  return {
    idOrName: parsed.form === 'id' ? parsed.entityId : parsed.entitySlug,
    raw: addr,
  };
}

/**
 * Resolve the `<app>` argument. Priority:
 *   1. Explicit argument → parsed as a CallTarget
 *   2. .cloudgrid/link.json → the linked entity's slug
 *   3. Neither → CloudGridError with the next step
 *
 * Pre-0.8.7 link.json files lack `entity_slug`; those throw a re-link hint
 * rather than silently mis-composing the address.
 */
export function resolveAppTarget(appArg: string | undefined): CallTarget {
  if (appArg && appArg.trim().length > 0) {
    return parseCallTarget(appArg.trim());
  }
  const link = readLink();
  if (!link) {
    throw new CloudGridError(
      'No app given and this directory is not linked to one. Pass the app address, or run from a linked directory.',
    );
  }
  if (!link.entity_slug) {
    throw new CloudGridError(
      "The link in this directory is from an older CLI and doesn't record the entity slug. Re-link with grid plug, or pass the app address.",
    );
  }
  return parseCallTarget(link.entity_slug);
}

// ── Entity lookup ─────────────────────────────────────────────────────────

/**
 * Fetch the canonical runtime detail for an address. Translates the 404
 * path into a §23-voice "no entity at <address>" error; other errors
 * propagate. Same-grid only — when the address points outside the client's
 * active grid, the server returns 404 (UUID-leak guard); the user sees the
 * same friendly copy.
 */
export async function lookupCallEntity(
  client: IV2Client,
  target: CallTarget,
): Promise<V2RuntimeDetailResponse> {
  try {
    return await client.get<V2RuntimeDetailResponse>(
      `/api/v2/runtimes/${encodeURIComponent(target.idOrName)}`,
    );
  } catch (err) {
    if (err instanceof V2ApiError && err.status === 404) {
      throw new CloudGridError(
        `No entity at ${target.raw}. Check the address or run grid get entities to see what's on your grid.`,
      );
    }
    throw err;
  }
}

/**
 * Map entity_id → name (the address slug) for every entity in the active
 * grid, archived included — a revoked caller may be an archived app, and its
 * row should still read as a name. Best effort: an id missing from the map
 * renders as the raw id.
 */
export async function fetchEntityNames(client: IV2Client): Promise<Map<string, string>> {
  const resp = await client.get<{ items: Array<{ entity_id: string; name: string }> }>(
    '/api/v2/runtimes',
    { kind: 'all', limit: 100, include_archived: 'true' },
  );
  const names = new Map<string, string>();
  for (const item of resp.items ?? []) {
    names.set(item.entity_id, item.name);
  }
  return names;
}

// ── Grant listing + matching ──────────────────────────────────────────────

/** Which side of a grant this command reads: the callee's or the caller's. */
export type GrantSide = 'inbound' | 'outbound';

/** The entity on the OTHER side of the grant, relative to the app at hand. */
export function counterpartId(grant: CallerGrant, side: GrantSide): string {
  return side === 'inbound' ? grant.from_entity_id : grant.to_entity_id;
}

/**
 * Resolve a `<caller>` / `<target>` argument to the grants it names. The
 * argument is a grant id (exact match wins) or an address in the shared
 * grammar. Slug forms match via the entity-name map; the id form matches
 * the grant's entity id directly, so it works even for entities the name
 * map no longer lists.
 */
export function matchGrants(
  grants: CallerGrant[],
  arg: string,
  names: Map<string, string>,
  side: GrantSide,
): CallerGrant[] {
  const byGrantId = grants.filter((g) => g.grant_id === arg);
  if (byGrantId.length > 0) return byGrantId;

  const target = parseCallTarget(arg);
  return grants.filter((g) => {
    const id = counterpartId(g, side);
    return id === target.idOrName || names.get(id) === target.idOrName;
  });
}

// ── Rendering ─────────────────────────────────────────────────────────────

/**
 * §23 status wording. Inbound, a `requested` grant is the callee admin's
 * move — say so. Outbound, the wait is on the other side; plain `pending`.
 */
export function grantStatusLabel(status: CallerGrant['status'], side: GrantSide): string {
  if (status === 'requested') {
    return side === 'inbound' ? 'pending your approval' : 'pending';
  }
  return status; // approved | revoked | rejected read fine as-is
}

/** The date the current status took effect, as YYYY-MM-DD. */
export function grantStatusDate(grant: CallerGrant): string {
  const iso =
    grant.status === 'approved' ? grant.approved_at
    : grant.status === 'revoked' ? grant.revoked_at
    : grant.status === 'rejected' ? grant.rejected_at
    : grant.requested_at;
  return iso ? iso.slice(0, 10) : '—';
}

/** Sort keys: pending first (it wants action), then approved, terminal last. */
const STATUS_ORDER: Record<CallerGrant['status'], number> = {
  requested: 0,
  approved: 1,
  revoked: 2,
  rejected: 3,
};

export function sortGrants(
  grants: CallerGrant[],
  names: Map<string, string>,
  side: GrantSide,
): CallerGrant[] {
  const nameOf = (g: CallerGrant) => names.get(counterpartId(g, side)) ?? counterpartId(g, side);
  return [...grants].sort((a, b) =>
    STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || nameOf(a).localeCompare(nameOf(b)),
  );
}

/**
 * Render the grants table. First column is the counterpart entity (CALLER
 * inbound, CALLS outbound); `-o wide` adds the grant id and entity id.
 */
export function renderGrantsTable(
  grants: CallerGrant[],
  names: Map<string, string>,
  side: GrantSide,
  wide: boolean,
): string {
  const first = side === 'inbound' ? 'CALLER' : 'CALLS';
  const header = wide
    ? [first, 'STATUS', 'SINCE', 'GRANT_ID', 'ENTITY_ID']
    : [first, 'STATUS', 'SINCE'];
  const data = grants.map((g) => {
    const id = counterpartId(g, side);
    const base = [names.get(id) ?? id, grantStatusLabel(g.status, side), grantStatusDate(g)];
    return wide ? [...base, g.grant_id, id] : base;
  });
  const widths = header.map((h, i) => Math.max(h.length, ...data.map((row) => row[i].length)));
  const fmt = (row: string[]) =>
    row.map((cell, i) => cell.padEnd(widths[i])).join('  ').trimEnd();
  const lines = [fmt(header), fmt(widths.map((w) => '-'.repeat(w)))];
  for (const row of data) lines.push(fmt(row));
  return lines.join('\n');
}

/** Structured (`--json` / `-o json|yaml`) row shape shared by both lists. */
export function serializeGrant(
  grant: CallerGrant,
  names: Map<string, string>,
  side: GrantSide,
): Record<string, unknown> {
  const id = counterpartId(grant, side);
  const nameKey = side === 'inbound' ? 'caller' : 'target';
  return {
    grant_id: grant.grant_id,
    [nameKey]: names.get(id) ?? id,
    [`${nameKey}_id`]: id,
    status: grant.status,
    since: grantStatusDate(grant),
    requested_at: grant.requested_at,
    approved_at: grant.approved_at,
    revoked_at: grant.revoked_at,
    rejected_at: grant.rejected_at,
    revocation_reason: grant.revocation_reason,
  };
}
